/**
 * CalendarView Component
 *
 * Displays production sessions in a calendar grid format.
 * Supports two modes: 1-week view (default) and full month view.
 * Sessions appear as colored event cards within each day cell.
 * Click dropdown arrow on card to see full details in a popout.
 * Color coding: planned=purple, ready=cyan, active=green, paused=yellow, completed=gray, cancelled=red
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, ChevronDown, X } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';
import { getDateCode } from '../../../utils/dateUtils';
import { getStatusDotHoverClass, getStatusBorderClass } from '../../../utils/statusColors';

interface CalendarViewProps {
  sessions: any[];
  onSessionClick: (session: any) => void;
  onDateSelect?: (date: string) => void;
  /** Called when a date cell is double-clicked (e.g. to create a session for that date) */
  onDateDoubleClick?: (date: string) => void;
  /** Called when the visible date range changes (navigation or mode toggle) with [startYYYY-MM-DD, endYYYY-MM-DD] */
  onRangeChange?: (startDate: string, endDate: string) => void;
  /** Currently selected date (highlighted in calendar). Null = no single-day selection */
  selectedDate?: string | null;
  /** Day the week starts on: 0 = Sunday (default), 1 = Monday */
  weekStartsOn?: 0 | 1;
  /** Custom range options in days (e.g. [7, 14, 30]). Overrides default 1week/month toggle. */
  rangeOptions?: number[];
  /** Default range in days when using rangeOptions (defaults to first item in rangeOptions) */
  defaultRange?: number;
  /** Optional className for the outer container (e.g. 'h-full flex flex-col' to stretch) */
  className?: string;
  /** When true, use taller cells (for full-width expanded layout) */
  expanded?: boolean;
}

type CalendarRangeMode = '1week' | 'month';

const CalendarView: React.FC<CalendarViewProps> = ({ sessions, onSessionClick, onDateSelect, onDateDoubleClick, onRangeChange, selectedDate: externalSelectedDate, weekStartsOn = 0, rangeOptions, defaultRange, className, expanded }) => {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [rangeMode, setRangeMode] = useState<CalendarRangeMode>('1week');
  const [rangeDays, setRangeDays] = useState<number>(defaultRange ?? rangeOptions?.[0] ?? 7);
  const useCustomRange = !!rangeOptions;
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const popoutRef = useRef<HTMLDivElement>(null);

  // Close popout when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoutRef.current && !popoutRef.current.contains(event.target as Node)) {
        setExpandedSessionId(null);
      }
    };
    if (expandedSessionId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedSessionId]);

  // Get calendar days based on range mode
  const calendarDays = useMemo(() => {
    // Helper: offset from weekStartsOn (0=Sun, 1=Mon)
    const dayOffset = (dayOfWeek: number) => (dayOfWeek - weekStartsOn + 7) % 7;

    if (useCustomRange) {
      // Custom day range: start from the week-start day of current week, show N days padded to full weeks
      const today = new Date(currentDate);
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOffset(today.getDay()));

      // Pad to full weeks
      const totalDays = Math.ceil(rangeDays / 7) * 7;
      const days: Date[] = [];
      const current = new Date(startDate);
      for (let i = 0; i < totalDays; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return days;
    }

    if (rangeMode === '1week') {
      // 1-week view: current week only
      const today = new Date(currentDate);
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOffset(today.getDay()));

      const days: Date[] = [];
      const current = new Date(startDate);

      // 7 days (1 week)
      for (let i = 0; i < 7; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      return days;
    } else {
      // Month view
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      // First day of the month
      const firstDay = new Date(year, month, 1);
      // Last day of the month
      const lastDay = new Date(year, month + 1, 0);

      // Start from weekStartsOn day of the week containing the first day
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - dayOffset(startDate.getDay()));

      // End on last day of the week containing the last day
      const endDate = new Date(lastDay);
      const daysUntilWeekEnd = (6 - dayOffset(endDate.getDay())) % 7;
      endDate.setDate(endDate.getDate() + daysUntilWeekEnd);

      const days: Date[] = [];
      const current = new Date(startDate);

      while (current <= endDate) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      return days;
    }
  }, [currentDate, rangeMode, weekStartsOn, useCustomRange, rangeDays]);

  // Notify parent of visible date range changes
  useEffect(() => {
    if (onRangeChange && calendarDays.length > 0) {
      const start = formatDateKey(calendarDays[0]);
      const end = formatDateKey(calendarDays[calendarDays.length - 1]);
      onRangeChange(start, end);
    }
  }, [calendarDays]);

  // Group sessions by date
  const sessionsByDate = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    sessions.forEach((session) => {
      const date = session.scheduled_date?.split('T')[0];
      if (date) {
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(session);
      }
    });
    return grouped;
  }, [sessions]);

  const navigate = (direction: number) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (useCustomRange) {
        // Navigate by the number of days in the current range
        const weeks = Math.ceil(rangeDays / 7);
        newDate.setDate(newDate.getDate() + (direction * weeks * 7));
      } else if (rangeMode === '1week') {
        // Navigate by 1 week
        newDate.setDate(newDate.getDate() + (direction * 7));
      } else {
        // Navigate by month
        newDate.setMonth(newDate.getMonth() + direction);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get header text based on range mode
  const getHeaderText = () => {
    const fmtCompact = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
    // Month-level views: show "April 2026" instead of date range
    if ((useCustomRange && rangeDays >= 28) || (!useCustomRange && rangeMode === 'month')) {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (useCustomRange || rangeMode === '1week') {
      const start = calendarDays[0];
      const end = calendarDays[calendarDays.length - 1];
      const showYear = start.getFullYear() !== new Date().getFullYear();
      return `${fmtCompact(start)} – ${fmtCompact(end)}${showYear ? `, ${end.getFullYear()}` : ''}`;
    }
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatDateKey = (date: Date): string => {
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };


  const allDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekDays = [...allDays.slice(weekStartsOn), ...allDays.slice(0, weekStartsOn)];

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${className || ''}`}>
      {/* Calendar Header — Today left, date nav center, week/month right */}
      <div className="grid grid-cols-3 items-center gap-2 p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
        {/* Left: Today button */}
        <div className="flex items-center justify-start">
          <button
            onClick={goToToday}
            className="px-3 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
          >
            {t('planning.today')}
          </button>
        </div>
        {/* Center: navigation arrows + date label */}
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            className="relative z-10 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400 hidden sm:block" />
            <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white min-w-[180px] md:min-w-[220px] text-center">
              {getHeaderText()}
            </h2>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
            className="relative z-10 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        {/* Right: range toggle */}
        <div className="flex items-center justify-end">
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 text-xs">
            {useCustomRange ? (
              rangeOptions!.map((days) => (
                <button
                  key={days}
                  onClick={() => setRangeDays(days)}
                  className={`px-2.5 py-1 rounded-md transition-colors ${
                    rangeDays === days
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                  }`}
                >
                  {days}
                </button>
              ))
            ) : (
              <>
                <button
                  onClick={() => setRangeMode('1week')}
                  className={`px-2.5 py-1 rounded-md transition-colors ${
                    rangeMode === '1week'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                  }`}
                >
                  {t('planning.oneWeek')}
                </button>
                <button
                  onClick={() => setRangeMode('month')}
                  className={`px-2.5 py-1 rounded-md transition-colors ${
                    rangeMode === 'month'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                  }`}
                >
                  {t('planning.month')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Week day headers */}
        <div className="grid grid-cols-7 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days grid */}
        <div className="grid grid-cols-7 gap-1 flex-1">
          {calendarDays.map((date, index) => {
            const dateKey = formatDateKey(date);
            const daySessions = sessionsByDate[dateKey] || [];
            const isCurrentMonthDay = isCurrentMonth(date);
            const isTodayDate = isToday(date);
            const isSelected = externalSelectedDate === dateKey;

            return (
              <div
                key={index}
                onClick={() => onDateSelect?.(dateKey)}
                onDoubleClick={() => onDateDoubleClick?.(dateKey)}
                className={`${expanded ? (useCustomRange ? (rangeDays <= 7 ? 'min-h-[200px]' : rangeDays <= 14 ? 'min-h-[160px]' : 'min-h-[120px]') : (rangeMode === '1week' ? 'min-h-[200px]' : 'min-h-[160px]')) : (useCustomRange ? (rangeDays <= 7 ? 'min-h-[150px]' : rangeDays <= 14 ? 'min-h-[160px]' : 'min-h-[90px]') : (rangeMode === '1week' ? 'min-h-[150px]' : 'min-h-[120px]'))} flex flex-col border rounded-lg p-1.5 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400 dark:border-indigo-500 ring-2 ring-indigo-400 ring-inset'
                    : isCurrentMonthDay
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800'
                } ${isTodayDate && !isSelected ? 'ring-2 ring-indigo-500 ring-inset' : ''}`}
              >
                {/* Day header with number and session count badge */}
                <div className="flex items-center justify-between mb-1">
                  <div
                    className={`text-sm font-medium ${
                      isTodayDate
                        ? 'text-indigo-600 dark:text-indigo-400'
                        : isCurrentMonthDay
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-gray-600'
                    }`}
                  >
                    {date.getDate()}
                  </div>
                  {daySessions.length > 0 && (
                    <div className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      daySessions.length >= 3
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                        : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                    }`}>
                      {daySessions.length}
                    </div>
                  )}
                </div>

                {/* Session events */}
                <div className="space-y-1 overflow-y-auto flex-1 min-h-0">
                  {daySessions.slice(0, 3).map((session: any) => {
                    const effectiveStatus = session.status;
                    // WCO-2273: show the ORIGINAL requested cases (set at session creation,
                    // stored per sub-session in target_output_cases by WCO-2315) instead of
                    // total_boxes, which gets recalculated when raw material is received.
                    // Fall back to total_boxes for legacy sessions where target_output_cases is null.
                    const originalCases = (session.sub_sessions || [])
                      .reduce((sum: number, s: any) => sum + (Number(s.target_output_cases) || 0), 0);
                    const cases = originalCases || session.total_boxes || 0;
                    const inputWeight = Number(session.initial_material_weight) || 0;
                    const outputWeight = Number(session.target_output_weight) || 0;
                    const materialItem = session.raw_material_item_number || '';
                    const materialName = session.raw_material_name || '';
                    const materialChineseName = session.raw_material_chinese_name || '';
                    const productName = session.product_name || '';
                    const productChineseName = session.product_chinese_name || '';
                    const isExpanded = expandedSessionId === session.session_number;

                    return (
                      <div key={session.session_number} className="relative">
                        {/* Compact card - clicking opens Session Details modal directly */}
                        <div
                          className={`text-xs px-1.5 py-1 rounded text-white cursor-pointer transition-colors border-l-2 ${getStatusDotHoverClass(effectiveStatus)} ${getStatusBorderClass(effectiveStatus)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSessionClick(session);
                          }}
                        >
                          {/* Header row with SKU and dropdown */}
                          <div className="flex items-center justify-between gap-1">
                            <div className="font-medium truncate flex-1">
                              {session.product_sku}
                            </div>
                            <div className="p-0.5 flex-shrink-0">
                              <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                          {/* Product name */}
                          <div className="text-[10px] opacity-90 truncate">
                            {productName}{productChineseName ? ` ${productChineseName}` : ''}
                          </div>
                          {/* Cases and output */}
                          <div className="text-[10px] opacity-90 truncate">
                            {cases > 0 ? `${cases} cs` : ''}{cases > 0 && outputWeight > 0 ? ' • ' : ''}{outputWeight > 0 ? `${outputWeight.toFixed(2)} lbs` : ''}
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>
                {daySessions.length > 3 && (
                  <div className="mt-1 flex-shrink-0 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded px-1.5 py-0.5 text-center">
                    +{daySessions.length - 3} {t('planning.more')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 pb-4">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-500 dark:text-gray-400">{t('planning.legend')}:</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-500"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusPending')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-purple-500"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusSubmitted')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-indigo-500"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusReceived')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-cyan-500"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusReady')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusActive')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-yellow-500"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusPaused')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-gray-400"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusCompleted')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-400"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('planning.statusCancelled')}</span>
          </div>
        </div>
      </div>

      {/* Session Details Popout Modal */}
      {expandedSessionId && (() => {
        const session = sessions.find((s: any) => s.session_number === expandedSessionId);
        if (!session) return null;

        const cases = session.total_boxes || 0;
        const inputWeight = session.initial_material_weight ? Number(session.initial_material_weight).toFixed(2) : '0';
        const outputWeight = session.target_output_weight ? Number(session.target_output_weight).toFixed(2) : '0';
        const materialItem = session.raw_material_item_number || '';
        const materialName = session.raw_material_name || '';
        const materialChineseName = session.raw_material_chinese_name || '';
        const productName = session.product_name || '';
        const productChineseName = session.product_chinese_name || '';
        const effectiveStatus = session.status;
        const packing = session.packing || '-';
        const brand = session.brand || '-';
        const lotNumber = session.raw_material_lot || session.transfer_number || session.lot_number || '-';
        const location = session.material_location_code || '-';
        const yieldFactorValue = Number(session.raw_material_yield_factor) || 0;
        const yieldFactor = yieldFactorValue > 0 ? formatYield(yieldFactorValue) : '?';
        const createdBy = session.created_by_name || '-';
        const createdAt = session.created_at ? new Date(session.created_at).toLocaleString() : '-';
        const scheduledDate = session.scheduled_date ? new Date(session.scheduled_date.split('T')[0] + 'T00:00:00').toLocaleDateString() : '-';

        const dateCode = session.scheduled_date ? getDateCode(session.scheduled_date) : '-';

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setExpandedSessionId(null)}
            />
            {/* Popout content */}
            <div
              ref={popoutRef}
              className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setExpandedSessionId(null)}
                className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors z-10"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>

              {/* Schedule Information Header */}
              <div className="bg-gray-50 dark:bg-gray-900/50 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="font-semibold text-gray-900 dark:text-white">{t('sessionDetails.scheduleInfo')}</span>
                  <span className={`ml-auto mr-8 inline-block px-2 py-0.5 text-xs font-medium rounded-full text-white ${getStatusDotHoverClass(effectiveStatus)}`}>
                    {t(`status.${effectiveStatus}`) || effectiveStatus}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.date')}</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{scheduledDate}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.dateCode')}</div>
                    <div className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{dateCode}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.createdBy')}</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{createdBy}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.createdAt')}</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{createdAt}</div>
                  </div>
                </div>
              </div>

              {/* Product and Material Info */}
              <div className="p-5">
                <div className="grid grid-cols-2 gap-4">
                  {/* Product Information */}
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-100 dark:border-indigo-800">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-5 h-5 rounded bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center">
                        <span className="text-xs">📦</span>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">{t('planning.productInfo')}</span>
                    </div>
                    <div className="mb-3">
                      <span className="text-base font-bold text-gray-900 dark:text-white">{session.product_sku}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-300 ml-2">{productName}</span>
                      {productChineseName && <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{productChineseName}</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.estimatedOutput')}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white mt-auto">{outputWeight} lbs</div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.packing')}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white mt-auto">{packing}</div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.estimatedCases')}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white mt-auto">{cases}</div>
                      </div>
                    </div>
                  </div>

                  {/* Material Information */}
                  <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-xs">🥩</span>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">{t('planning.materialInfo')}</span>
                    </div>
                    <div className="mb-2">
                      <span className="text-base font-bold text-gray-900 dark:text-white">{materialItem || '-'}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-300 ml-2">{materialName}</span>
                      {materialChineseName && <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{materialChineseName}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-3">
                      <span>{t('sessionDetails.brand')}: {brand}</span>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span>{t('sessionDetails.yieldFactor')}: {yieldFactor}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.lotNumber')}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{lotNumber}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.initialWeight')}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{inputWeight} lbs</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('sessionDetails.location')}</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{location}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* View Details Button */}
                <button
                  onClick={() => {
                    setExpandedSessionId(null);
                    onSessionClick(session);
                  }}
                  className="w-full mt-4 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                >
                  {t('planning.viewFullDetails')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CalendarView;
